import { isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, PLATFORM_ID, effect, inject, input, output } from '@angular/core';

type CompletedImageLoadingStatus = 'loaded' | 'error';
export type ImageLoadingStatus = 'loading' | 'delayed' | CompletedImageLoadingStatus;

@Directive({
  selector: 'img[sharedUtilsDirectiveImageLoadingStatus]',
  host: {
    '(error)': 'finishLoading("error")',
    '(load)': 'finishLoading("loaded")',
  },
})
export class ImageLoadingStatusDirective {
  private static readonly DELAY_MS = 1000;

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly image = inject<ElementRef<HTMLImageElement>>(ElementRef).nativeElement;
  private delayTimer: ReturnType<typeof setTimeout> | undefined;

  readonly loadingKey = input.required<string>();
  readonly loadingActive = input(true);

  readonly loadingStatusChange = output<ImageLoadingStatus>();

  constructor() {
    effect((onCleanup) => {
      const loadingActive = this.loadingActive();

      if (!this.isBrowser) return;

      this.clearDelayTimer();
      this.loadingStatusChange.emit('loading');

      if (!loadingActive) return;

      this.trackImageLoading();

      onCleanup(() => this.clearDelayTimer());
    });
  }

  protected finishLoading(status: CompletedImageLoadingStatus): void {
    this.clearDelayTimer();
    this.loadingStatusChange.emit(status);
  }

  private trackImageLoading(): void {
    if (!this.loadingKey()) return;

    if (this.image.complete) {
      this.loadingStatusChange.emit(this.image.naturalWidth > 0 ? 'loaded' : 'error');
      return;
    }

    this.delayTimer = setTimeout(() => {
      this.delayTimer = undefined;
      this.loadingStatusChange.emit('delayed');
    }, ImageLoadingStatusDirective.DELAY_MS);
  }

  private clearDelayTimer(): void {
    if (this.delayTimer !== undefined) {
      clearTimeout(this.delayTimer);
      this.delayTimer = undefined;
    }
  }
}
